import PropTypes from "prop-types";
import MDBox from "theme/components/MDBox";
import MDInput from "theme/components/MDInput";
import { useState, useEffect } from "react";
import { Snackbar, Alert, ListItem, ListItemText, Icon } from "@mui/material";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

function DOBField({ formRef, draftIndex, displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errMsg, setErrMsg] = useState("Invalid date of birth provided.");

  function calculateAge(birthday) {
    // birthday is a date
    const ageDifMs = Date.now() - birthday;
    const ageDate = new Date(ageDifMs); // miliseconds from epoch
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }

  const [hasSavedValue, setHasSavedValue] = useState(false);
  const [draftValue, setDraftValue] = useState(null);

  useEffect(() => {
    if (!formRef) return;

    const saved = localStorage.getItem(`draft_${formRef}`);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      const val = parsed?.[draftIndex].values?.[fieldRef];

      if (val !== undefined && val !== null && val !== "") {
        setDraftValue(val);
        setHasSavedValue(true);
      }
    } catch (e) {
      console.error("Draft parse error", e);
    }
  }, [formRef, fieldRef]);

  return (
    <MDBox>
      {hasSavedValue ? (
        <MDBox>
          <ListItem divider>
            <MDBox mr={1}>
              <Icon color="success">check_circle</Icon>
            </MDBox>
            <ListItemText
              primary={
                <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                  {displayName}
                </MDTypography>
              }
              secondary={
                <MDTypography variant="body2" fontSize={13}>
                  {draftValue}
                </MDTypography>
              }
            />
            <MDButton
              size="small"
              color="warning"
              variant="outlined"
              iconOnly
              onClick={() => {
                try {
                  const key = `draft_${formRef}`;
                  const saved = localStorage.getItem(key);
                  if (!saved) return;

                  const parsed = JSON.parse(saved);

                  if (parsed?.values && fieldRef in parsed.values) {
                    delete parsed.values[fieldRef];
                  }

                  // persist updated draft
                  localStorage.setItem(key, JSON.stringify(parsed));

                  // update UI state
                  setHasSavedValue(false);
                  setDraftValue(null);
                } catch (e) {
                  console.error("Failed to clear draft field", e);
                }
              }}
            >
              <Icon>edit</Icon>
            </MDButton>
          </ListItem>
          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={draftValue}
          />
        </MDBox>
      ) : (
        <MDBox>
          <MDInput
            name={fieldRef}
            id={`dff_${fieldRef}`}
            margin="dense"
            label={displayName}
            type="date"
            fullWidth
            variant="outlined"
            onBlur={(e) => {
              const dob = e.target.value;
              const dobDate = new Date(dob);
              const age = calculateAge(dobDate);

              if (typeof options.min_age !== "undefined") {
                let ageMin = options.min_age;
                if (age < ageMin) {
                  e.target.value = null;
                  setErrMsg(`A minimum age of ${ageMin} is required`);
                  showFieldError(true);
                  return;
                }
              }

              if (typeof options.max_age !== "undefined") {
                if (age > options.max_age) {
                  e.target.value = null;
                  setErrMsg(`A maximum age of ${options.max_age} is required`);
                  showFieldError(true);
                }
              }
            }}
            options={options}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={5000}
            onClose={() => showFieldError(false)}
            open={fieldError}
          >
            <Alert severity="error" sx={{ width: "100%" }}>
              {errMsg}
            </Alert>
          </Snackbar>
        </MDBox>
      )}
    </MDBox>
  );
}

// Typechecking props of the Transaction
DOBField.propTypes = {
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

DOBField.defaultProps = {
  options: null,
};

export default DOBField;
